// src/components/oficina/OperatorsConfigPanel.js
import React, { useState, useEffect } from "react";
import { Users, Plus, Edit2, Trash2, Shield, Key, Clock, Check } from "lucide-react";
import { pb } from "../../services/pocketbase";
import { hashPin } from "../../utils/operatorUtils";
import { useOperator } from "../../contexts/OperatorContext";

const PRESET_COLORS = [
  { name: "Slate", hex: "#64748b" },
  { name: "Red", hex: "#ef4444" },
  { name: "Orange", hex: "#f97316" },
  { name: "Amber", hex: "#f59e0b" },
  { name: "Emerald", hex: "#10b981" },
  { name: "Teal", hex: "#14b8a6" },
  { name: "Blue", hex: "#3b82f6" },
  { name: "Indigo", hex: "#6366f1" },
  { name: "Violet", hex: "#8b5cf6" },
  { name: "Pink", hex: "#ec4899" },
];

export default function OperatorsConfigPanel() {
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [editingId, setEditingId] = useState(null);
  const [nome, setNome] = useState("");
  const [pin, setPin] = useState("");
  const [cor, setCor] = useState(PRESET_COLORS[0].hex);
  const [colaborador, setColaborador] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [timeoutMinutos, setTimeoutMinutos] = useState(5);
  const [tipo, setTipo] = useState("OFICINA");

  // Carrega dados iniciais
  const loadData = async () => {
    setLoading(true);
    try {
      const opRecords = await pb.collection("oficina_operadores").getFullList({
        sort: "nome",
        expand: "colaborador",
      });
      const userRecords = await pb.collection("users").getFullList({
        sort: "name",
      });
      setOperators(opRecords);
      setUsers(userRecords);
    } catch (e) {
      console.error("Erro ao carregar dados dos operadores:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setNome("");
    setPin("");
    setCor(PRESET_COLORS[0].hex);
    setColaborador("");
    setAtivo(true);
    setTimeoutMinutos(5);
    setTipo("OFICINA");
  };

  const handleEdit = (op) => {
    setEditingId(op.id);
    setNome(op.nome || "");
    setPin(""); // Deixa em branco, só preenche se for alterar
    setCor(op.cor || PRESET_COLORS[0].hex);
    setColaborador(op.colaborador || "");
    setAtivo(op.ativo !== false);
    setTimeoutMinutos(op.timeout_minutos || 5);
    setTipo(op.tipo || "OFICINA");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;

    const mins = Number(timeoutMinutos);
    if (isNaN(mins) || mins < 1) {
      alert("O tempo de inatividade deve ser de no mínimo 1 minuto.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nome: nome.toUpperCase().trim(),
        cor,
        colaborador: colaborador || null,
        ativo,
        timeout_minutos: mins,
        tipo,
      };

      // Se inseriu PIN (obrigatório na criação, opcional na edição)
      if (pin) {
        if (pin.length !== 4 || isNaN(pin)) {
          alert("O PIN deve ter exatamente 4 números.");
          setLoading(false);
          return;
        }
        payload.pin = await hashPin(pin);
      } else if (!editingId) {
        alert("O PIN é obrigatório para cadastrar um novo operador.");
        setLoading(false);
        return;
      }

      if (editingId) {
        await pb.collection("oficina_operadores").update(editingId, payload);
      } else {
        await pb.collection("oficina_operadores").create(payload);
      }

      resetForm();
      await loadData();
    } catch (err) {
      console.error("Erro ao salvar operador:", err);
      alert("Erro ao salvar operador: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este operador?")) return;
    setLoading(true);
    try {
      await pb.collection("oficina_operadores").delete(id);
      await loadData();
    } catch (e) {
      console.error("Erro ao excluir operador:", e);
      alert("Erro ao excluir operador: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      
      {/* COLUNA ESQUERDA: LISTA DE OPERADORES */}
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="font-bold text-slate-700 text-sm flex items-center gap-1.5 border-b pb-2">
          <Users size={16} /> Operadores Cadastrados
        </h3>

        {loading && operators.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-xs font-semibold">
            Carregando operadores...
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-50 text-slate-600 font-bold uppercase border-b">
                <tr>
                  <th className="p-3">Operador</th>
                  <th className="p-3">Vínculo de Usuário</th>
                  <th className="p-3 text-center">Tipo</th>
                  <th className="p-3 text-center">Inatividade</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {operators.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-6 text-center text-slate-400">
                      Nenhum operador cadastrado. Use o formulário ao lado para adicionar.
                    </td>
                  </tr>
                ) : (
                  operators.map((op) => (
                    <tr key={op.id} className="hover:bg-slate-50 text-slate-700">
                      <td className="p-3 font-bold text-slate-900 flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm"
                          style={{ backgroundColor: op.cor || "#64748b" }}
                        >
                          {op.nome?.substring(0, 2).toUpperCase()}
                        </div>
                        <span>{op.nome}</span>
                      </td>
                      <td className="p-3 text-slate-500 font-medium">
                        {op.expand?.colaborador ? (
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold">
                            {op.expand.colaborador.name} ({op.expand.colaborador.username})
                          </span>
                        ) : (
                          <em className="text-slate-400">Sem vínculo</em>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          op.tipo === "PDV" 
                            ? "bg-purple-100 text-purple-800" 
                            : "bg-blue-100 text-blue-800"
                        }`}>
                          {op.tipo || "OFICINA"}
                        </span>
                      </td>
                      <td className="p-3 text-center text-slate-650 font-bold">
                        {op.timeout_minutos || 5} min
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            op.ativo !== false
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {op.ativo !== false ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(op)}
                            className="p-1 hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(op.id)}
                            className="p-1 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* COLUNA DIREITA: FORMULÁRIO */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="font-bold text-slate-700 text-sm flex items-center gap-1.5 border-b pb-2">
          {editingId ? <Edit2 size={16} /> : <Plus size={16} />}
          {editingId ? "Editar Operador" : "Adicionar Operador"}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Nome */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">Nome:</label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-slate-800 uppercase"
              placeholder="Ex: MARCOS"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>

          {/* PIN */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">
              PIN (4 dígitos):
            </label>
            <div className="relative">
              <Key className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="password"
                maxLength={4}
                required={!editingId}
                className="w-full pl-8 pr-3 py-2 border rounded-lg outline-none focus:border-slate-800 font-bold"
                placeholder={editingId ? "Preencha apenas se for alterar" : "Ex: 1234"}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <p className="text-[9px] text-slate-400 mt-1">
              O PIN será armazenado de forma criptografada por Hash SHA-256.
            </p>
          </div>

          {/* Vínculo de Usuário */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">Vínculo com Usuário (Colaborador):</label>
            <select
              className="w-full px-3 py-2 border rounded-lg bg-white outline-none focus:border-slate-800"
              value={colaborador}
              onChange={(e) => setColaborador(e.target.value)}
            >
              <option value="">Nenhum</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.username})
                </option>
              ))}
            </select>
          </div>

          {/* Tipo de Terminal */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">
              Tipo de Terminal:
            </label>
            <select
              className="w-full px-3 py-2 border rounded-lg bg-white outline-none focus:border-slate-800 font-bold"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option value="OFICINA">OFICINA</option>
              <option value="PDV">PDV</option>
            </select>
            <p className="text-[9px] text-slate-400 mt-1">
              Define em qual terminal este operador estará disponível para identificação.
            </p>
          </div>

          {/* Tempo de Inatividade (minutos) */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">
              Tempo de Inatividade (minutos):
            </label>
            <input
              type="number"
              min={1}
              max={60}
              required
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-slate-800 font-bold"
              placeholder="Padrão: 5"
              value={timeoutMinutos}
              onChange={(e) => setTimeoutMinutos(e.target.value)}
            />
            <p className="text-[9px] text-slate-400 mt-1">
              Tempo ocioso até deslogar e bloquear a tela para este operador.
            </p>
          </div>

          {/* Seletor de Cores */}
          <div>
            <label className="block font-bold text-slate-500 uppercase mb-1">Cor do Card (Avatar):</label>
            <div className="flex flex-wrap gap-2.5 mt-1.5">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.hex}
                  type="button"
                  onClick={() => setCor(color.hex)}
                  className={`w-6 h-6 rounded-full border-2 transition-transform ${
                    cor === color.hex ? "border-slate-900 scale-110 shadow" : "border-transparent"
                  }`}
                  style={{ backgroundColor: color.hex }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Checkbox Ativo */}
          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700 py-1">
            <input
              type="checkbox"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
            />
            Operador Ativo
          </label>

          {/* Ações */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={resetForm}
              className="border px-4 py-2 rounded-lg font-bold hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold disabled:opacity-50"
            >
              {editingId ? "Salvar Alterações" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}
